import React, { useState } from "react";
import { EmailContext, HistoryItem } from "../../types";
import ContextStrip from "../components/ContextStrip";
import ActionCard from "../components/ActionCard";
import RecentScores from "../components/RecentScores";
import styles from "./HomeView.module.css";

interface HomeViewProps {
  emailContext: EmailContext | null;
  onAnalyse: () => void;
  onRewrite: () => void;
  onWrite: () => void;
  onClose: () => void;
  onLogout: () => void;
  onRefresh?: () => void | Promise<void>;
  onOpenHistory?: (item: HistoryItem) => Promise<void> | void;
}

function BarChartIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="13" width="3.5" height="8" rx="1" fill="#4f6af0" />
      <rect x="8.5" y="9" width="3.5" height="12" rx="1" fill="#16a34a" />
      <rect x="14" y="5" width="3.5" height="16" rx="1" fill="#f59e0b" />
      <rect x="19.5" y="11" width="3" height="10" rx="1" fill="#ea580c" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={spinning ? styles.spin : ""}
    >
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </svg>
  );
}

export default function HomeView({
  emailContext,
  onAnalyse,
  onRewrite,
  onWrite,
  onLogout,
  onRefresh,
  onOpenHistory,
}: HomeViewProps) {
  const [showNoDraftMsg, setShowNoDraftMsg] = useState(false);
  const [showNoContextMsg, setShowNoContextMsg] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const canAnalyse = emailContext?.hasDraft === true;
  const isComposeOpen = emailContext?.isCompose === true;
  const hasContext = emailContext !== null;

  // A compose pane open with an empty body is a different situation from no
  // compose pane at all — the message should tell the user which one it is.
  const noDraftMessage = isComposeOpen
    ? "Your draft is empty. Write something in the email body, then analyze."
    : "No draft detected. Open a compose window or reply in Outlook first.";

  const handleNoDraftClick = () => {
    setShowNoDraftMsg(true);
    setTimeout(() => setShowNoDraftMsg(false), 3000);
  };

  const handleNoContextClick = () => {
    setShowNoContextMsg(true);
    setTimeout(() => setShowNoContextMsg(false), 3000);
  };

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setRefreshing(false), 600);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.body}>
        <div className={styles.strip}>
          <ContextStrip context={emailContext} />
        </div>

        <div className={styles.sectionRow}>
          <span className={styles.sectionLabel}>What would you like to do?</span>
          <div className={styles.divider} />
          {onRefresh && (
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Refresh email context"
              title="Refresh email context"
            >
              <RefreshIcon spinning={refreshing} />
            </button>
          )}
        </div>

        {showNoDraftMsg && (
          <div className={styles.noDraftToast}>{noDraftMessage}</div>
        )}

        {showNoContextMsg && (
          <div className={styles.noDraftToast}>
            No email open — open an email in Outlook to get started.
          </div>
        )}

        <div className={styles.cardStack}>
        <ActionCard
          index={0}
          icon={<BarChartIcon />}
          iconVariant="blue"
          title="Analyze and Improve My Email"
          description={
            canAnalyse
              ? "Score your draft on Trusted Partner principles plus the ASLAN phase for your goal, with specific coaching"
              : isComposeOpen
                ? "Write something in the email body to enable analysis"
                : "Start writing a reply or new email to enable analysis"
          }
          onClick={onAnalyse}
          disabled={!canAnalyse}
          onDisabledClick={!canAnalyse ? handleNoDraftClick : undefined}
        />
        {/* "Improve my email" card hidden for now — to be revisited in future. */}
        <ActionCard
          index={1}
          icon={<PencilIcon />}
          iconVariant="yellow"
          title="Write email for me"
          description="ASLAN drafts a new email based on the thread context and selected prompts."
          onClick={onWrite}
          disabled={!hasContext}
          onDisabledClick={!hasContext ? handleNoContextClick : undefined}
        />
        </div>

        <RecentScores onOpen={onOpenHistory} />
      </div>
      <div className={styles.footer}>
        <button className={styles.signOutBtn} onClick={onLogout}>
          Sign out
        </button>
      </div>
    </div>
  );
}
